import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HomeService, ProductNameService } from 'src/app/services';
import { 
  AddReleaseItemRequest, AddReleaseItemResponse, GetHomeConfigRequest, GetHomeConfigResponse,
  ListReleasesRequest, ListReleasesResponse, ListStyleTemplatesRequest, ListStyleTemplatesResponse, ProductName, Release, 
  SituViewWebApiClient, StyleConfigTemplate
} from 'situview-webapi-client';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectableItem } from 'src/app/shared/models/selectable-item';

@Component({
  selector: 'release-style-template-dialog',
  templateUrl: './release-style-template-dialog.component.html',
  styleUrls: ['./release-style-template-dialog.component.less']
})
export class ReleaseStyleTemplateDialogComponent {
  productNames = ProductName;
  selectedProductName: ProductName | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReleaseStyleTemplateDialogComponentData,
    private dialogRef: MatDialogRef<ReleaseStyleTemplateDialogComponent>,
    private homeService: HomeService,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient,
    private productNameService: ProductNameService) {
    this.selectedProductName = this.data.productName;
    this.loadStyleTemplates();
    this.loadReleases();
  }

  selectProductName(productName: ProductName) {
    this.selectedProductName = productName;
  }
  
  selectTemplate(template: SelectableItem) {
    let selectableItem = this.filteredStyleConfigTemplates?.find(x => x.item === template.item);
    if (selectableItem) {
      selectableItem.selected = !selectableItem.selected;
    }
  }

  selectRelease(releaseId: string) {
    const index = this.selectedReleases.findIndex(x => x === releaseId);
    if (index === -1) {
      this.selectedReleases.push(releaseId);
    } else {
      this.selectedReleases.splice(index, 1);
    }
  }

  searchTemplatesField = new FormControl();
  styleConfigTemplates: SelectableItem[] | undefined;
  filteredStyleConfigTemplates: SelectableItem[] | undefined;
  doneSelectingTemplates: boolean = false;
  getSelectedItemCount(): number {
    return this.filteredStyleConfigTemplates?.filter(item => item.selected)?.length ?? 0;
  }
  loadStyleTemplates() {
    //this.loading = true;

    let request = new ListStyleTemplatesRequest();
    request.companyId = this.data.companyId;
    request.isHidden = false;
    request.showAll = true;
    if (this.selectedProductName !== undefined) request.productName = this.selectedProductName;

    this.situViewApiClient.listStyleTemplates(request)
      .then((response: ListStyleTemplatesResponse) => {
        if (response.success && response.styleTemplates) {
          this.styleConfigTemplates = [...new Set(
            response.styleTemplates
              .map((x: StyleConfigTemplate) => x.name ?? "")
              .filter((x: string) => x)
          )].map(x => new SelectableItem(x, false));
          this.filteredStyleConfigTemplates = [...this.styleConfigTemplates];
        } else {
          this.showMessage("There was an issue retrieving style templates!");
        }
        //this.loading = false;
      })
      .catch((err: any) => {
        //this.loading = false;
        this.showMessage("Could not retrieve style templates!");
      });
  }
  doTemplateFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredStyleConfigTemplates = this.styleConfigTemplates?.filter(e => { return e.item?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredStyleConfigTemplates = this.styleConfigTemplates;
    }
  }

  searchReleasesField = new FormControl();
  releases: Release[] | undefined;
  filteredReleases: Release[] | undefined;
  selectedReleases: string[] = [];
  doneSelectingReleases: boolean = false;
  isReleaseSelected(releaseId: string): boolean {
    return this.selectedReleases.includes(releaseId);
  }
  loadReleases() {
    //this.loading = true;

    let request = new ListReleasesRequest();
    request.companyId = this.data.companyId;
    request.isNotReleased = true;

    this.situViewApiClient.listReleases(request)
      .then((response: ListReleasesResponse) => {
        if (response.success && response.items) {
          this.releases = response.items;
          this.filteredReleases = [...this.releases];
        } else {
          this.showMessage("There was an issue retrieving releases!");
        }
        //this.loading = false;
      })
      .catch((err: any) => {
        //this.loading = false;
        this.showMessage("Could not retrieve releases!");
      });
  }
  doReleaseFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredReleases = this.releases?.filter(e => { return e.name?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredReleases = this.releases;
    }
  }

  /* releases */
  async addReleaseItems() {
    let releaseItemCount = 0;
    const promises: Promise<any>[] = [];
  
    this.data.homeConfigIds.forEach((homeConfigId: string) => {
      let getHomeConfigRequest = new GetHomeConfigRequest();
      getHomeConfigRequest.homeConfigId = homeConfigId;
  
      const homeConfigPromise = this.homeService.GetHomeConfig(getHomeConfigRequest)
        .then((getHomeConfigResponse: GetHomeConfigResponse) => {
          if (getHomeConfigResponse.success && getHomeConfigResponse?.homeId) {
            const stylePromises = this.filteredStyleConfigTemplates
              ?.filter(x => x.selected)
              .map((styleTemplate: SelectableItem) => {
                return this.selectedReleases.map((releaseId: string) => {
                  return this.addReleaseItem(releaseId, getHomeConfigResponse.homeId!, homeConfigId, styleTemplate.item)
                    .then((addReleaseItemResponse: AddReleaseItemResponse) => {
                      if (addReleaseItemResponse.success) {
                        releaseItemCount = releaseItemCount + 1;
                      }
                    });
                });
              });
  
            const flattenedStylePromises = stylePromises ? stylePromises.reduce((acc, val) => acc.concat(val), []) : [];
            return Promise.all(flattenedStylePromises);
          }
        });
  
      promises.push(homeConfigPromise);
    });
  
    await Promise.all(promises);
  
    this.dialogRef.close({ releaseItemCount: releaseItemCount, releaseCount: this.selectedReleases.length } as ReleaseStyleTemplateDialogComponentResultData);
  }

  private addReleaseItem(releaseId: string, homeId: string, homeConfigId: string, styleTemplate: string) {
    let request = new AddReleaseItemRequest();
    request.companyId = this.data.companyId;
    request.releaseId = releaseId;
    request.homeId = homeId;
    request.homeConfigId = homeConfigId;
    request.styleConfigTemplate = styleTemplate;
    request.productName = this.selectedProductName;

    return this.situViewApiClient.addReleaseItem(request);
  }
  /* releases */

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName)?.productColor;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export interface ReleaseStyleTemplateDialogComponentData {
  companyId: string;
  productName: ProductName;
  homeConfigIds: string[];
}

export interface ReleaseStyleTemplateDialogComponentResultData {
  releaseItemCount: number | undefined;
  releaseCount: number | undefined;
}
